import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { mgcIconDownRegular } from '@sb/mgc-icons/mgcIcon-down_regular.icon';
import { mgcIconFilter2Regular } from '@sb/mgc-icons/mgcIcon-filter_2_regular.icon';
import { mgcIconRightRegular } from '@sb/mgc-icons/mgcIcon-right_regular.icon';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import uniqueId from 'lodash-es/uniqueId';

import { countSectionItems, type DepartmentSections } from '../requests/department-sections.helper';
import { SidebarHeaderComponent } from '../sidebar-header.component';

const NO_DEPARTMENT_KEY = 'no-department';

/**
 * One status list of requests (e.g. "Pending requests"), split into a collapsible section per
 * selected department. Sections start closed, so the user first sees an overview of all
 * departments; with `expandFirst`, the first department is open from the start. The sections the
 * user opened stay open while the rows change, and a section that drops out starts closed if it
 * comes back. The projected `<ng-template let-group>` renders a section's rows from `group.items`
 * (and `group.alsoIn`).
 */
@Component({
  selector: 'department-sections',
  templateUrl: './department-sections.component.html',
  imports: [NgTemplateOutlet, TranslatePipe, IconComponent, SidebarHeaderComponent],
})
export class DepartmentSectionsComponent<T extends { id?: string }> implements OnInit {
  title = input.required<string>();
  sections = input.required<DepartmentSections<T>>();
  /** Opens the first department when the list first renders; only the departments listed then count. */
  expandFirst = input(false);

  rows = contentChild.required(TemplateRef);

  idPrefix = uniqueId('department-sections-');

  /** Open sections, limited to those still listed. */
  openSectionKeys = linkedSignal<DepartmentSections<T>, Set<string>>({
    source: this.sections,
    computation: (sections, previous) => {
      const listedKeys = new Set([
        ...[...sections.groups, ...sections.otherGroups].map((group) => group.departmentId),
        ...(sections.noDepartmentItems.length > 0 ? [NO_DEPARTMENT_KEY] : []),
      ]);

      return new Set([...(previous?.value ?? [])].filter((key) => listedKeys.has(key)));
    },
  });

  /** Whether the sections for departments outside the selection are listed too. */
  showOtherDepartments = signal(false);

  itemCount = computed(() => countSectionItems(this.sections()));

  /** `name: null` renders as the translated "No department". */
  noDepartmentGroup = computed(() => {
    const items = this.sections().noDepartmentItems;

    return items.length > 0
      ? { departmentId: NO_DEPARTMENT_KEY, name: null, locationName: null, label: null, items, alsoIn: {} }
      : null;
  });

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconDownRegular, mgcIconFilter2Regular, mgcIconRightRegular]);
  }

  ngOnInit(): void {
    const [firstGroup] = this.sections().groups;

    if (this.expandFirst() && firstGroup) {
      this.openSectionKeys.set(new Set([firstGroup.departmentId]));
    }
  }

  toggleSection(key: string): void {
    this.openSectionKeys.update((keys) => {
      const nextKeys = new Set(keys);

      if (!nextKeys.delete(key)) {
        nextKeys.add(key);
      }

      return nextKeys;
    });
  }
}
